import { getFirestore, collection, query, orderBy, limit, getDocs, doc, getDoc, updateDoc, serverTimestamp, startAfter } from 'firebase/firestore';
import { app } from '../firebase';

const db = getFirestore(app);

export const adminMessageService = {
  /**
   * Fetch contact messages with pagination
   * @param {Object} params - Parameters for fetching messages
   * @param {number} params.limit - Number of messages to fetch
   * @param {Object} params.startAfter - Document to start after for pagination
   * @returns {Promise<Object>} Messages data with pagination info
   */
  async fetchMessages(params = {}) {
    try {
      const { limit: limitCount = 50, startAfter: startAfterDoc } = params;
      
      // Simple query without complex filters to avoid indexes
      let q = collection(db, 'contact_messages');
      
      // Apply pagination
      if (startAfterDoc) {
        q = query(q, startAfter(startAfterDoc));
      }
      
      q = query(q, orderBy('createdAt', 'desc'), limit(limitCount));
      
      const snapshot = await getDocs(q);
      
      const messages = [];
      snapshot.forEach(doc => {
        const messageData = doc.data();
        messages.push({
          id: doc.id,
          ...messageData,
          createdAt: messageData.createdAt?.toDate?.() || messageData.createdAt,
          updatedAt: messageData.updatedAt?.toDate?.() || messageData.updatedAt
        });
      });
      
      console.log('Fetched messages:', messages);
      return {
        messages,
        hasMore: messages.length === limitCount,
        lastDoc: snapshot.docs[snapshot.docs.length - 1]
      };
      
    } catch (error) {
      console.error('Error fetching messages:', error);
      throw new Error(error.message || 'Failed to fetch messages');
    }
  },

  /**
   * Get detailed information about a specific message
   * @param {string} messageId - The message ID
   * @returns {Promise<Object>} Message details
   */
  async fetchMessageDetails(messageId) {
    try {
      const messageDoc = await getDoc(doc(db, 'contact_messages', messageId));
      
      if (!messageDoc.exists()) {
        throw new Error('Message not found');
      }

      const messageData = messageDoc.data();
      
    //   // Get user details if available
    //   let userData = null;
    //   if (messageData.userId) {
    //     try {
    //       const userDoc = await getDoc(doc(db, 'users', messageData.userId));
    //       if (userDoc.exists()) {
    //         userData = userDoc.data();
    //       }
    //     } catch (error) {
    //       console.warn('Could not fetch user data:', error);
    //     }
    //   }

      console.log('Message details:', messageData);
      return {
        message: {
          id: messageDoc.id,
          ...messageData,
          createdAt: messageData.createdAt?.toDate?.() || messageData.createdAt,
          updatedAt: messageData.updatedAt?.toDate?.() || messageData.updatedAt
        },
        user: null // userData is not available since user fetching is commented out
      };
      
    } catch (error) {
      console.error('Error fetching message details:', error);
      throw new Error(error.message || 'Failed to fetch message details');
    }
  },

  /**
   * Update message status
   * @param {string} messageId - The message ID
   * @param {string} status - New status ('read', 'replied', 'archived')
   * @param {string} notes - Optional admin notes
   * @returns {Promise<Object>} Updated message data
   */
  async updateMessageStatus(messageId, status, notes = null) {
    try {
      const validStatuses = ['unread', 'read', 'replied', 'archived'];
      if (!validStatuses.includes(status)) {
        throw new Error('Invalid status');
      }

      const updateData = {
        status,
        updatedAt: serverTimestamp()
      };

      if (notes) {
        updateData.adminNotes = notes;
      }

      // Add status history
      const messageRef = doc(db, 'contact_messages', messageId);
      const messageDoc = await getDoc(messageRef);
      
      if (!messageDoc.exists()) {
        throw new Error('Message not found');
      }

      const currentData = messageDoc.data();
      const statusHistory = currentData.statusHistory || [];
      
      statusHistory.push({
        status,
        timestamp: new Date(),
        notes: notes || null
      });

      updateData.statusHistory = statusHistory;

      await updateDoc(messageRef, updateData);

      // Get updated message data
      const updatedDoc = await getDoc(messageRef);
      const updatedData = updatedDoc.data();

      return {
        success: true,
        message: {
          id: updatedDoc.id,
          ...updatedData,
          createdAt: updatedData.createdAt?.toDate?.() || updatedData.createdAt,
          updatedAt: updatedData.updatedAt?.toDate?.() || updatedData.updatedAt
        }
      };
      
    } catch (error) {
      console.error('Error updating message status:', error);
      throw new Error(error.message || 'Failed to update message status');
    }
  },

  /**
   * Delete a message (soft delete)
   * @param {string} messageId - The message ID
   * @param {string} reason - Reason for deletion
   * @returns {Promise<Object>} Success status
   */
  async deleteMessage(messageId, reason = null) {
    try {
      await updateDoc(doc(db, 'contact_messages', messageId), {
        deleted: true,
        deletedAt: serverTimestamp(),
        deleteReason: reason || null
      });

      return { success: true };
      
    } catch (error) {
      console.error('Error deleting message:', error);
      throw new Error(error.message || 'Failed to delete message');
    }
  },

  /**
   * Get message statistics
   * @param {string} period - Time period ('today', 'week', 'month')
   * @returns {Promise<Object>} Message statistics
   */
  async fetchMessageStats(period = 'today') {
    try {
      let startDate;
      const now = new Date();
      
      switch (period) {
        case 'today':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        case 'week':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
          break;
        case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
          break;
        default:
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      }

      // Simple query without complex filters to avoid indexes
      const q = query(
        collection(db, 'contact_messages'),
        orderBy('createdAt', 'desc'),
        limit(1000) // Get more results to calculate stats
      );
      
      const snapshot = await getDocs(q);

      const stats = {
        totalMessages: 0,
        unreadMessages: 0,
        readMessages: 0,
        repliedMessages: 0,
        archivedMessages: 0,
        recentMessages: 0 // Messages in the specified period
      };

      snapshot.forEach(doc => {
        const messageData = doc.data();
        
        // Filter by date in memory
        const messageDate = messageData.createdAt?.toDate?.() || messageData.createdAt;
        if (messageDate && messageDate >= startDate) {
          stats.recentMessages++;
        }
        
        // Skip deleted messages
        if (messageData.deleted) {
          return;
        }
        
        stats.totalMessages++;
        
        // Count by status
        switch (messageData.status) {
          case 'unread':
            stats.unreadMessages++;
            break;
          case 'read':
            stats.readMessages++;
            break;
          case 'replied':
            stats.repliedMessages++;
            break;
          case 'archived':
            stats.archivedMessages++;
            break;
        }
      });

      return stats;
      
    } catch (error) {
      console.error('Error fetching message stats:', error);
      throw new Error(error.message || 'Failed to fetch message statistics');
    }
  },

  /**
   * Search messages
   * @param {string} searchQuery - Search query
   * @param {number} limit - Maximum number of results
   * @returns {Promise<Object>} Search results
   */
  async searchMessages(searchQuery, limitCount = 20) {
    try {
      if (!searchQuery || searchQuery.trim().length < 2) {
        throw new Error('Search query must be at least 2 characters');
      }

      const queryText = searchQuery.trim().toLowerCase();
      
      // Simple query without complex filters to avoid indexes
      const q = query(
        collection(db, 'contact_messages'),
        orderBy('createdAt', 'desc'),
        limit(100) // Get more results to search through
      );

      const snapshot = await getDocs(q);
      
      // Search in memory instead of using complex queries
      const allMessages = [];
      snapshot.forEach(doc => {
        const messageData = doc.data();
        
        // Check if message matches search criteria
        const matchesSearch = 
          (messageData.name && messageData.name.toLowerCase().includes(queryText)) ||
          (messageData.email && messageData.email.toLowerCase().includes(queryText)) ||
          (messageData.phone && messageData.phone.includes(queryText)) ||
          (messageData.message && messageData.message.toLowerCase().includes(queryText));
        
        if (matchesSearch) {
          allMessages.push({
            id: doc.id,
            ...messageData,
            createdAt: messageData.createdAt?.toDate?.() || messageData.createdAt
          });
        }
      });

      // Sort by creation date and limit results
      const messages = allMessages
        .sort((a, b) => b.createdAt - a.createdAt)
        .slice(0, limitCount);

      return { messages };
      
    } catch (error) {
      console.error('Error searching messages:', error);
      throw new Error(error.message || 'Failed to search messages');
    }
  },

  /**
   * Get status badge configuration
   * @returns {Object} Status badge styles
   */
  getStatusBadgeConfig() {
    return {
      unread: {
        bgColor: 'bg-red-500',
        textColor: 'text-white',
        dotColor: 'bg-red-600',
        label: 'Unread'
      },
      read: {
        bgColor: 'bg-blue-500',
        textColor: 'text-white',
        dotColor: 'bg-blue-600',
        label: 'Read'
      },
      replied: {
        bgColor: 'bg-green-500',
        textColor: 'text-white',
        dotColor: 'bg-green-600',
        label: 'Replied'
      },
      archived: {
        bgColor: 'bg-gray-500',
        textColor: 'text-white',
        dotColor: 'bg-gray-600',
        label: 'Archived'
      }
    };
  },

  /**
   * Format message data for display
   * @param {Object} message - Raw message data
   * @returns {Object} Formatted message data
   */
  formatMessageForDisplay(message) {
    const statusConfig = this.getStatusBadgeConfig();
    const status = statusConfig[message.status] || statusConfig.unread;
    
    return {
      ...message,
      // Format time and date
      formattedTime: message.createdAt ? new Date(message.createdAt).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      }) : '',
      formattedDate: message.createdAt ? new Date(message.createdAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }) : '',
      relativeTime: message.createdAt ? this.getRelativeTime(message.createdAt) : '',
      statusConfig,
      displayStatus: status,
      // Truncate message for preview
      messagePreview: message.message ? 
        (message.message.length > 50 ? message.message.substring(0, 50) + '...' : message.message) : 
        'No message'
    };
  },

  /**
   * Get relative time (e.g., "2 hours ago")
   * @param {Date} date - The date to compare
   * @returns {string} Relative time string
   */
  getRelativeTime(date) {
    const now = new Date();
    const messageDate = new Date(date);
    const diffInSeconds = Math.floor((now - messageDate) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else {
      return messageDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  },

  /**
   * Calculate message summary statistics
   * @param {Array} messages - Array of messages
   * @returns {Object} Summary statistics
   */
  calculateMessageSummary(messages) {
    const summary = {
      totalMessages: messages.length,
      unreadCount: 0,
      readCount: 0,
      repliedCount: 0,
      archivedCount: 0
    };

    messages.forEach(message => {
      switch (message.status) {
        case 'unread':
          summary.unreadCount++;
          break;
        case 'read':
          summary.readCount++;
          break;
        case 'replied':
          summary.repliedCount++;
          break;
        case 'archived':
          summary.archivedCount++;
          break;
      }
    });

    return summary;
  }
};

export default adminMessageService; 